/**
 * "Waiting on you" — the owner's quote chip (Corbin, 10/28).
 *
 * Close Out jobs sat more than 3 days because quotes waited on Corbin and no
 * one place showed him all of them. This chip lives in his toolbar (alerts:
 * one home each — never the bell, never a phone push):
 *   · hidden when nothing waits; blue when everything is fresh, amber at 2
 *     days, red at 3+ — the oldest sets the color;
 *   · click opens a flyout, oldest first: customer, site, total, how long,
 *     who asked; one tap **Approve** (the same approve as Close Out, every
 *     gate still applies) or **Open to fix**.
 *
 * The chip and the flyout read ONE server list (GET /api/owner/quotes-waiting).
 * The org OWNER only (Settings → Organization → Owner; Corbin, 10/28): other
 * Admins get nothing, and with no owner set the chip stays hidden.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, FileSignature, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDollars } from "@/lib/utils";

export interface WaitingQuote {
    estimateId: string;
    displayNumber: string | null;
    jobId: string | null;
    jobNumber: string | null;
    customer: string;
    site: string | null;
    total: number;
    kind: "pending" | "changed";
    approvedTotal: number | null;
    since: string;
    days: number;
    askedBy: string | null;
}

export interface QuotesWaitingView {
    /** False for anyone who isn't the org owner: the chip is the owner's alone. */
    forYou: boolean;
    count: number;
    oldestDays: number | null;
    totalValue: number;
    items: WaitingQuote[];
    approverName: string | null;
}

export const QUOTES_WAITING_KEY = ["owner", "quotes-waiting"] as const;

export type WaitTone = "blue" | "amber" | "red";

/** Color by how long it has waited: 3+ days red (the complaint), 2 amber, else blue. */
export function waitTone(days: number): WaitTone {
    if (days >= 3) return "red";
    if (days >= 2) return "amber";
    return "blue";
}

export function waitWords(days: number): string {
    if (days <= 0) return "Today";
    return days === 1 ? "1 day" : `${days} days`;
}

/** The chip: hidden at zero; the oldest quote sets the color. */
export function quotesChipState(view: QuotesWaitingView | undefined): { show: boolean; tone: WaitTone; label: string } {
    const n = view?.count ?? 0;
    if (!view || !view.forYou || n === 0) return { show: false, tone: "blue", label: "No quotes to OK" };
    return {
        show: true,
        tone: waitTone(view.oldestDays ?? 0),
        label: `Quotes to OK · ${n}`,
    };
}

export function openToFixHref(q: Pick<WaitingQuote, "jobId" | "estimateId">): string {
    return q.jobId ? `/close-out?job=${q.jobId}&station=3` : `/commercial/${q.estimateId}`;
}

export function QuotesWaitingChip() {
    const { isAdmin } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: QUOTES_WAITING_KEY,
        queryFn: () => api.get<QuotesWaitingView>("/api/owner/quotes-waiting"),
        enabled: !!isAdmin,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
        retry: false,
    });
    const [open, setOpen] = useState(false);
    // Why an Approve was refused, by quote, so the card can say it in place.
    const [blocked, setBlocked] = useState<Record<string, string>>({});
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const approve = useMutation({
        mutationFn: (estimateId: string) => api.post(`/api/close-out/estimates/${estimateId}/approve`, {}),
        onSuccess: (_r, estimateId) => {
            const item = q.data?.items.find((i) => i.estimateId === estimateId);
            toast.success(`Approved${item ? ` — ${item.customer}` : ""}. It's back with the office to send.`);
            setBlocked((b) => {
                const { [estimateId]: _gone, ...rest } = b;
                return rest;
            });
            void qc.invalidateQueries({ queryKey: QUOTES_WAITING_KEY });
            void qc.invalidateQueries({ queryKey: ["close-out-queue"] });
        },
        onError: (e: any, estimateId) => {
            // Every approval gate still applies; say why, and point at the fix.
            setBlocked((b) => ({
                ...b,
                [estimateId]: e?.body?.message ?? "This one can't be approved as it is. Open it to fix.",
            }));
        },
    });

    if (!isAdmin) return null;
    const view = q.data;
    const state = quotesChipState(view);
    if (!state.show && !open) return null;
    const items = view?.items ?? [];

    return (
        <>
            <button
                type="button"
                className={`qwchip t-${state.tone}`}
                data-testid="quotes-waiting-chip"
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`${state.label}${view?.oldestDays ? `, oldest ${waitWords(view.oldestDays)}` : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <FileSignature size={15} aria-hidden />
                <span>{state.label}</span>
            </button>
            <aside
                className={`bkfly${open ? " on" : ""}`}
                role="dialog"
                aria-label="Quotes waiting on you"
                aria-hidden={!open}
                {...((open ? {} : { inert: "" }) as Record<string, string>)}
                data-testid="quotes-waiting-flyout"
            >
                <div className="bkfly-h">
                    <b>Waiting on you</b>
                    <button type="button" className="x" aria-label="Close" onClick={() => setOpen(false)}>
                        <X size={16} />
                    </button>
                </div>
                <div className="bkfly-b">
                    <div className="legend" aria-hidden>
                        <span>
                            <i style={{ background: "#B23B36" }} />3 days or more
                        </span>
                        <span>
                            <i style={{ background: "#C9772B" }} />2 days
                        </span>
                        <span>
                            <i style={{ background: "#004AAD" }} />
                            New
                        </span>
                    </div>
                    {items.length === 0 ? (
                        <p className="empty">No quotes waiting on you. New ones show up here.</p>
                    ) : (
                        items.map((item) => {
                            const tone = waitTone(item.days);
                            const why = blocked[item.estimateId];
                            const busy = approve.isPending && approve.variables === item.estimateId;
                            return (
                                <article
                                    key={item.estimateId}
                                    className={`bc ${tone}`}
                                    data-testid="quotes-waiting-card"
                                >
                                    <div className="bc-top">
                                        <div>
                                            <b>{item.customer}</b>
                                            <span className="s">
                                                {[item.site, item.displayNumber ? `Quote #${item.displayNumber}` : null]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </span>
                                        </div>
                                        <span className="when">{waitWords(item.days)}</span>
                                    </div>
                                    <div className="qw-money">
                                        <b>{formatDollars(item.total)}</b>
                                        {item.kind === "changed" && item.approvedTotal != null && (
                                            <span className="handoff">
                                                Changed since you approved {formatDollars(item.approvedTotal)}
                                            </span>
                                        )}
                                    </div>
                                    {item.askedBy && <span className="owner">{item.askedBy} asked</span>}
                                    {why && (
                                        <span className="lockwhy" data-testid="quotes-waiting-why">
                                            {why}
                                        </span>
                                    )}
                                    <div className="bc-acts">
                                        <button
                                            type="button"
                                            className="fb sched"
                                            disabled={busy || !!why}
                                            onClick={() => approve.mutate(item.estimateId)}
                                            data-testid="quotes-waiting-approve"
                                        >
                                            {busy ? (
                                                <Loader2 size={14} className="animate-spin" aria-hidden />
                                            ) : (
                                                <Check size={14} aria-hidden />
                                            )}
                                            Approve
                                        </button>
                                        <button
                                            type="button"
                                            className="fb contact"
                                            onClick={() => {
                                                setOpen(false);
                                                navigate(openToFixHref(item));
                                            }}
                                            data-testid="quotes-waiting-open"
                                        >
                                            Open to fix
                                        </button>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>
            </aside>
        </>
    );
}

/** Scoped to the toolbar (.tbv3). The flyout reuses the booking flyout's look. */
export const quotesWaitingChipCss = `
  .tbv3 .qwchip{display:inline-flex;align-items:center;justify-content:center;gap:7px;height:34px;padding:0 13px;
    border-radius:10px;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap;border:1px solid;
    font-variant-numeric:tabular-nums}
  .tbv3 .qwchip.t-blue{background:#004AAD;color:#fff;border-color:#004AAD}
  .tbv3 .qwchip.t-amber{background:#C9772B;color:#fff;border-color:#C9772B}
  .tbv3 .qwchip.t-red{background:#B23B36;color:#fff;border-color:#B23B36}
  .tbv3 .qwchip:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  .tbv3 .qw-money{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:15px}
  @media (max-width:720px){.tbv3 .qwchip{flex:1 1 auto}}
`;
